import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisHorizontalIcon, PencilIcon } from '@heroicons/react/24/outline';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { PolicySwitch } from '../accountModels/PolicySwitch';
import { EditModelProviderDialog } from '../modelcatalog/EditModelProviderDialog';
import { ProviderDisableBlockedDialog } from '../modelcatalog/ProviderDisableBlockedDialog';
import { blockingConfigNames, isDefaultProviderRefusal } from '../modelcatalog/catalogErrors';
import { useOptionalToast } from '../../contexts/ToastContext';
import { useFlag } from '../../hooks/useFeatureFlags';
import { useRemoveModelProviderKey, useUpdateModelProvider, useVerifyModelProvider } from '../../hooks/useModels';
import { asApiRefusal, refusalReason } from '../../utils/apiRefusal';
import type { Model, ModelProvider } from '../../types';
import { ProviderRow, platformKeyState } from './ProviderRow';
import { LIVE_SOURCE_PROVIDER_KEYS, plural } from './modelsPageData';

/**
 * The selected provider's card on the Models page (task 1167): name, Enabled
 * switch and platform-default mark; the fact line (ID — never "Key" — protocol,
 * base URL, whether the review checks it); then three rows:
 *
 * 1. **Platform key** — the shared `ProviderRow` in `platform` scope.
 * 2. **Account keys** — how many accounts bring their own key, and what
 *    disabling the provider does to them (omitted at zero).
 * 3. **Models** — counts by status, and the Review view for this provider.
 *
 * Edit and the menu (Test connection, Enable / Disable) keep the catalog's
 * behaviour, including the dependency-aware disable refusal.
 */

type Blocked = { providerName: string; configNames: string[]; message: string | null; defaultProvider?: boolean };

const MENU_ITEM_CLASSES =
  'flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 data-[focus]:bg-gray-50 dark:data-[focus]:bg-gray-700';

const ROW_LABEL = 'w-28 flex-shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';

const ADAPTER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic protocol',
  openai: 'OpenAI protocol',
  'openai-compatible': 'OpenAI-compatible protocol',
};

interface ProviderCardProps {
  provider: ModelProvider;
  models: Model[];
  canManage: boolean;
  /** The Review view for this provider, or null when Review is not available. */
  reviewPath: string | null;
}

export function ProviderCard({ provider, models, canManage, reviewPath }: ProviderCardProps) {
  // The platform key row stores credentials, so it has its own lever (task 1217).
  const platformKeysEnabled = useFlag('innovate.fe.model-catalog.platform-keys');
  const verify = useVerifyModelProvider();
  const update = useUpdateModelProvider();
  const removeKey = useRemoveModelProviderKey();
  const toast = useOptionalToast();
  const [editing, setEditing] = useState<{ focusKey: boolean } | null>(null);
  const [blocked, setBlocked] = useState<Blocked | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const keyState = platformKeyState(provider);
  const checkedByReview = LIVE_SOURCE_PROVIDER_KEYS.has(provider.key);
  const accountKeys = provider.accountKeyCount ?? 0;

  const byStatus = {
    active: models.filter((m) => m.status === 'active').length,
    deprecated: models.filter((m) => m.status === 'deprecated').length,
    retired: models.filter((m) => m.status === 'retired').length,
  };

  const testConnection = async () => {
    try {
      await verify.mutateAsync(provider.id);
    } catch (err) {
      toast?.error(`Could not test ${provider.displayName}`, refusalReason(err));
    }
  };

  const toggleEnabled = async () => {
    const enabling = !provider.enabled;
    try {
      await update.mutateAsync({ id: provider.id, data: { enabled: enabling } });
    } catch (err) {
      if (!enabling && asApiRefusal(err).status === 409) {
        setBlocked({
          providerName: provider.displayName,
          configNames: blockingConfigNames(err),
          message: refusalReason(err),
          defaultProvider: isDefaultProviderRefusal(err),
        });
        return;
      }
      toast?.error(`Could not ${enabling ? 'enable' : 'disable'} ${provider.displayName}`, refusalReason(err));
    }
  };

  const confirmRemoveKey = async () => {
    try {
      const result = await removeKey.mutateAsync(provider.id);
      toast?.success(
        'Stored key removed',
        result.fallbackToInstalled
          ? `${provider.displayName} calls use the installed key again.`
          : `${provider.displayName} has no key. Its calls fail until one is added.`,
      );
    } catch (err) {
      toast?.error('Could not remove the stored key', refusalReason(err));
    } finally {
      setConfirmingRemove(false);
    }
  };

  // What Remove will do, from the key source the read reported; the result's
  // `fallbackToInstalled` is what the toast reports afterwards.
  const fallsBack = keyState.source === 'stored-over-installed';

  return (
    <section
      data-testid={`models-provider-card-${provider.key}`}
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div
            aria-hidden="true"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-base font-bold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
          >
            {provider.displayName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{provider.displayName}</h2>
              <span
                data-testid={`models-provider-enabled-${provider.key}`}
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  provider.enabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {provider.enabled ? 'Enabled' : 'Disabled'}
              </span>
              {provider.isPlatformDefault && (
                <span
                  data-testid={`models-provider-default-${provider.key}`}
                  className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
                >
                  Platform default
                </span>
              )}
            </div>
            <p data-testid={`models-provider-facts-${provider.key}`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              ID <span className="font-mono">{provider.key}</span>
              {provider.adapter ? ` · ${ADAPTER_LABELS[provider.adapter] ?? provider.adapter}` : ''} ·{' '}
              {provider.baseUrl ? <span className="font-mono">{provider.baseUrl}</span> : 'Default base URL'} ·{' '}
              {checkedByReview ? 'Checked by the review' : 'Not checked by the review — limits and prices are hand-maintained'}
            </p>
          </div>
        </div>

        {canManage && (
          <div className="flex items-center gap-2">
            <PolicySwitch
              checked={provider.enabled}
              onToggle={toggleEnabled}
              disabled={update.isPending}
              label={`${provider.enabled ? 'Disable' : 'Enable'} ${provider.displayName}`}
              testId={`models-provider-toggle-${provider.key}`}
            />
            <button
              type="button"
              onClick={() => setEditing({ focusKey: false })}
              data-testid={`models-provider-edit-${provider.key}`}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <PencilIcon className="h-3.5 w-3.5" />
              Edit
            </button>
            <Menu as="div" className="relative inline-block text-left">
              <MenuButton
                data-testid={`provider-row-actions-${provider.key}`}
                aria-label={`Actions for ${provider.displayName}`}
                className="inline-flex items-center rounded-md p-1 text-gray-400 hover:text-gray-600 focus:outline-none dark:hover:text-gray-200"
              >
                <EllipsisHorizontalIcon className="h-5 w-5" />
              </MenuButton>
              <MenuItems className="absolute right-0 z-50 mt-2 w-44 origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800">
                <MenuItem disabled={verify.isPending}>
                  <button type="button" onClick={testConnection} className={MENU_ITEM_CLASSES}>
                    Test connection
                  </button>
                </MenuItem>
                <MenuItem disabled={update.isPending}>
                  <button type="button" onClick={toggleEnabled} className={MENU_ITEM_CLASSES}>
                    {provider.enabled ? 'Disable' : 'Enable'}
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        )}
      </div>

      <div className="mt-4 divide-y divide-gray-100 border-t border-gray-100 dark:divide-gray-700 dark:border-gray-700">
        {platformKeysEnabled && (
          <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
            <span className={ROW_LABEL}>Platform key</span>
            <div className="min-w-0 flex-1">
              <ProviderRow
                scope="platform"
                provider={provider}
                canManage={canManage}
                testing={verify.isPending}
                onTest={testConnection}
                onEditKey={() => setEditing({ focusKey: true })}
                onRemoveStoredKey={() => setConfirmingRemove(true)}
              />
            </div>
          </div>
        )}

        {accountKeys > 0 && (
          <div data-testid={`models-provider-account-keys-${provider.key}`} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
            <span className={ROW_LABEL}>Account keys</span>
            <p className="min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-300">
              {plural(accountKeys, 'account brings', 'accounts bring')} their own {provider.displayName} key · their calls
              never use the platform key · disabling this provider stops them too.
            </p>
          </div>
        )}

        <div data-testid={`models-provider-models-${provider.key}`} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
          <span className={ROW_LABEL}>Models</span>
          <p className="min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-300">
            {models.length} in the catalog · {byStatus.active} active
            {byStatus.deprecated ? ` · ${byStatus.deprecated} deprecated` : ''}
            {byStatus.retired ? ` · ${byStatus.retired} retired` : ''}
            {reviewPath && (
              <>
                {' · '}
                <Link to={reviewPath} className="text-indigo-600 hover:underline dark:text-indigo-400">
                  Review this provider
                </Link>
              </>
            )}
          </p>
        </div>
      </div>

      {canManage && (
        <>
          <EditModelProviderDialog
            provider={editing ? provider : null}
            focusKey={editing?.focusKey ?? false}
            onClose={() => setEditing(null)}
          />
          <ProviderDisableBlockedDialog blocked={blocked} onClose={() => setBlocked(null)} />
          <ConfirmDialog
            isOpen={confirmingRemove}
            onClose={() => setConfirmingRemove(false)}
            onConfirm={confirmRemoveKey}
            title={`Remove the stored ${provider.displayName} key?`}
            message={
              <p data-testid="remove-stored-key-message">
                {fallsBack ? (
                  <>
                    {provider.displayName} falls back to the installed key
                    {provider.installedKeyLastFour ? (
                      <>
                        {' '}
                        <span className="font-mono">····{provider.installedKeyLastFour}</span>
                      </>
                    ) : null}{' '}
                    read from the environment. Every account without its own key calls it on that key.
                  </>
                ) : (
                  <>
                    {provider.displayName} has no other key. Its calls fail until a key is added, for every account
                    without its own key.
                  </>
                )}
              </p>
            }
            confirmLabel="Remove stored key"
            confirmStyle="danger"
            isLoading={removeKey.isPending}
          />
        </>
      )}
    </section>
  );
}

export default ProviderCard;
